// Lightweight tone-shape feedback shared by the public tone trainer and the
// members' tone practice tool. Contours are [{ t, semitones }] relative to each
// speaker's own median, so the two curves are directly comparable as shapes. We
// resample both onto a common normalized-time grid and compare: for a moving
// tone, the correlation of the pitch tracks (does it rise/fall/dip together?);
// for a flat tone (ngang, where the reference barely moves and correlation is
// meaningless) we just check the learner held it steady. This is intentionally
// lightweight and encouraging — a nudge, not a grade.

function resample(points, n = 16) {
  if (!points || points.length < 2) return null
  const t0 = points[0].t
  const span = points[points.length - 1].t - t0 || 1
  const norm = points.map(p => ({ x: (p.t - t0) / span, y: p.semitones }))
  const out = []
  let j = 0
  for (let i = 0; i < n; i++) {
    const x = i / (n - 1)
    while (j < norm.length - 2 && norm[j + 1].x < x) j++
    const a = norm[j], b = norm[j + 1]
    const frac = b.x === a.x ? 0 : (x - a.x) / (b.x - a.x)
    out.push(a.y + (b.y - a.y) * frac)
  }
  return out
}

function std(arr) {
  const m = arr.reduce((s, v) => s + v, 0) / arr.length
  return Math.sqrt(arr.reduce((s, v) => s + (v - m) ** 2, 0) / arr.length)
}

function corr(a, b) {
  const n = a.length
  const ma = a.reduce((s, v) => s + v, 0) / n
  const mb = b.reduce((s, v) => s + v, 0) / n
  let num = 0, da = 0, db = 0
  for (let i = 0; i < n; i++) {
    num += (a[i] - ma) * (b[i] - mb)
    da += (a[i] - ma) ** 2
    db += (b[i] - mb) ** 2
  }
  if (da === 0 || db === 0) return null
  return num / Math.sqrt(da * db)
}

const FLAT_REF_ST = 1.2 // reference std below this reads as a "flat" tone (ngang)
const HOLD_ST = 2.0     // learner std below this counts as "held steady"

// `accent` names the reference in the copy ("Saigon", "Hanoi") — accent claims
// are fine, but never let this copy imply a native speaker (it's TTS).
export function scoreShape(userPoints, refPoints, accent = 'Saigon') {
  const u = resample(userPoints)
  const r = resample(refPoints)
  if (!u || !r) return { tier: 'ok', headline: 'Recorded', text: 'Compare your amber curve to the reference, then try again.' }

  if (std(r) < FLAT_REF_ST) {
    return std(u) < HOLD_ST
      ? { tier: 'great', headline: 'Nice and level', text: 'You held it steady and flat, just like the reference — that\'s the ngang tone.' }
      : { tier: 'close', headline: 'Almost', text: 'This tone stays flat and level — try to hold your pitch steady instead of letting it drift.' }
  }

  const c = corr(u, r)
  if (c === null) return { tier: 'ok', headline: 'Recorded', text: 'Compare your curve to the reference and give it another go.' }
  if (c >= 0.6)  return { tier: 'great', headline: 'Nailed it', text: `Your pitch tracks the ${accent} reference closely — that's the tone.` }
  if (c >= 0.2)  return { tier: 'close', headline: 'On the right track', text: 'The shape is close — match the direction of the reference curve and try again.' }
  return { tier: 'keep', headline: 'Keep at it', text: 'Watch which way the reference curve moves, then copy that direction on your next try.' }
}
